import classNames from 'classnames'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { TriangleRightFillIcon } from 'components/icons/TriangleRightFIll'
import type { ReactElement } from 'react'
import React from 'react'

function SortButton<T>({
  sortable,
  isCurrentStrategy,
}: {
  sortable?: BreakoutTableColumnHeaderSortableBase<T> & { sortingStrategy: T }
  isCurrentStrategy: boolean
}) {
  if (!sortable) return null

  return (
    <TriangleRightFillIcon
      size={12}
      className={classNames('shrink-0 rotate-90', {
        'rotate-90': isCurrentStrategy && sortable.currentOrder === 'desc',
        '!rotate-270': isCurrentStrategy && sortable.currentOrder === 'asc',
        'fill-on-surface-var': !isCurrentStrategy,
        'fill-on-surface': isCurrentStrategy,
      })}
    />
  )
}

function getSortDirection(
  isCurrentStrategy: boolean,
  sortable?: { currentOrder: 'asc' | 'desc' }
) {
  if (!sortable) return undefined
  if (!isCurrentStrategy) return undefined
  return sortable.currentOrder === 'asc'
    ? ('ascending' as const)
    : ('descending' as const)
}

export function BreakoutTableColumnHeader<T>({
  label,
  sortable,
  tooltip,
  alignment = 'left',
  className,
  labelClassName,
  colSpan,
  tooltipClassName,
  suffix,
  testId,
}: {
  label?: string | React.ReactNode
  tooltip?: string | ReactElement
  sortable?: BreakoutTableColumnHeaderSortableBase<T> & { sortingStrategy: T }
  alignment?: 'left' | 'center' | 'right'
  className?: string
  labelClassName?: string
  colSpan?: number
  tooltipClassName?: string
  suffix?: React.ReactNode
  testId?: string
}) {
  const isCurrentStrategy =
    sortable && sortable.currentSortingStrategy === sortable.sortingStrategy

  const handleSortClick = () => {
    if (!sortable) return

    if (isCurrentStrategy) {
      if (sortable.currentOrder === 'asc') {
        sortable.changeSortingStrategy(sortable.sortingStrategy, 'desc')
      } else if (
        sortable.currentOrder === 'desc' &&
        sortable.allowDefaultSortingStrategy
      ) {
        sortable.changeSortingStrategy('default', 'asc')
      } else {
        sortable.changeSortingStrategy(sortable.sortingStrategy, 'asc')
      }
    } else {
      sortable.changeSortingStrategy(sortable.sortingStrategy, 'asc')
    }
  }

  let labelContent: React.ReactNode

  if (typeof label === 'string') {
    labelContent = (
      <strong
        className={classNames(
          'text-label-medium',
          {
            'text-on-surface': isCurrentStrategy,
            'text-on-surface-var': !isCurrentStrategy,
            'select-none': sortable?.sortingStrategy !== undefined,
          },
          labelClassName
        )}
      >
        {(() => {
          if (!tooltip) return label

          const labelParts = label.split(' ')
          const hasMultipleParts = labelParts.length > 1
          const beforeLast = labelParts.slice(0, -1).join(' ')
          const last = labelParts[labelParts.length - 1]
          return (
            <>
              {beforeLast}
              {hasMultipleParts && ' '}
              <span className="whitespace-nowrap">
                {last}
                <BreakoutInfoTooltip
                  content={tooltip}
                  iconSize={15}
                  className={tooltipClassName}
                  iconClassName="shrink-0 ml-1 text-on-surface-var inline-block"
                />
              </span>
            </>
          )
        })()}
      </strong>
    )
  } else {
    labelContent = (
      <>
        {label}
        {tooltip ? (
          <BreakoutInfoTooltip
            content={tooltip}
            iconSize={15}
            className={tooltipClassName}
            iconClassName="shrink-0 ml-1 text-on-surface-var inline-block"
          />
        ) : null}
      </>
    )
  }

  return (
    <th
      className={className}
      scope="col"
      colSpan={colSpan}
      data-testid={testId}
      aria-sort={getSortDirection(isCurrentStrategy || false, sortable)}
    >
      <div
        className={classNames('flex items-center', {
          'justify-center': alignment === 'center',
          'justify-end': alignment === 'right',
        })}
      >
        <div
          className={classNames('flex flex-row items-center gap-1 bg-surface', {
            'cursor-pointer': sortable?.sortingStrategy,
            'justify-center text-center': alignment === 'center',
            'justify-end text-right': alignment === 'right',
          })}
          role={sortable ? 'button' : undefined}
          tabIndex={sortable ? 0 : undefined}
          onMouseDown={handleSortClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault() // Prevent scrolling on Space
              handleSortClick()
            }
          }}
        >
          {labelContent}
          {sortable?.sortingStrategy !== undefined && (
            <SortButton<T>
              sortable={sortable}
              isCurrentStrategy={isCurrentStrategy || false}
            />
          )}
        </div>
        {suffix}
      </div>
    </th>
  )
}

export type BreakoutTableColumnHeaderSortableBase<T> =
  | {
      currentOrder: 'asc' | 'desc'
      currentSortingStrategy: T | 'default'
      changeSortingStrategy: (
        strategy: T | 'default',
        order: 'asc' | 'desc'
      ) => void
      allowDefaultSortingStrategy: true
    }
  | {
      currentOrder: 'asc' | 'desc'
      currentSortingStrategy: T
      changeSortingStrategy: (strategy: T, order: 'asc' | 'desc') => void
      allowDefaultSortingStrategy?: false
    }
